import {
  ALL_DAYS,
  DAYS,
  DEFAULT_TIMESPAN,
  OPEN_24_HRS,
  WEEKDAYS,
  WEEKEND,
  addSpan,
  formatSpan,
  hasAnyHours,
  isOpen,
  isOpen24,
  joinSpan,
  openDayCount,
  removeSpan,
  setDays,
  splitSpan,
  toggle24,
  toggleDay,
  updateSpan
} from '../schedule';

const MON = 'mon_open_close';
const SUN = 'sun_open_close';

const hours = (overrides = {}) => ({
  mon_open_close: ['09:00-17:00'],
  tue_open_close: ['09:00-17:00'],
  wed_open_close: [],
  thu_open_close: [OPEN_24_HRS],
  fri_open_close: ['09:00-12:00', '13:00-17:00'],
  sat_open_close: [],
  sun_open_close: [],
  ...overrides
});

describe('day order', () => {
  it('starts on Monday and ends on Sunday', () => {
    expect(DAYS[0].key).toBe(MON);
    expect(DAYS[DAYS.length - 1].key).toBe(SUN);
    expect(DAYS).toHaveLength(7);
  });
});

describe('reading a day', () => {
  it('treats an empty list as closed', () => {
    expect(isOpen(hours(), 'wed_open_close')).toBe(false);
    expect(isOpen(hours(), MON)).toBe(true);
  });

  it('recognises 24 hours', () => {
    expect(isOpen24(hours(), 'thu_open_close')).toBe(true);
    expect(isOpen24(hours(), MON)).toBe(false);
  });

  it('counts only the open days', () => {
    expect(openDayCount(hours())).toBe(4);
    expect(openDayCount({})).toBe(0);
  });

  it('survives a missing day', () => {
    expect(isOpen({}, MON)).toBe(false);
    expect(isOpen(null as any, MON)).toBe(false);
  });
});

describe('toggling', () => {
  it('closing a day empties it, reopening gives one default span', () => {
    const closed = toggleDay(hours(), MON);
    expect(closed[MON]).toEqual([]);
    expect(toggleDay(closed, MON)[MON]).toEqual([DEFAULT_TIMESPAN]);
  });

  it('24 hours replaces the spans and toggles back to one default span', () => {
    const all_day = toggle24(hours(), MON);
    expect(all_day[MON]).toEqual([OPEN_24_HRS]);
    expect(toggle24(all_day, MON)[MON]).toEqual([DEFAULT_TIMESPAN]);
  });

  it('leaves the other days alone', () => {
    const next = toggleDay(hours(), MON);
    expect(next['tue_open_close']).toEqual(['09:00-17:00']);
  });
});

describe('spans', () => {
  it('adds and removes without touching the rest', () => {
    const added = addSpan(hours(), MON);
    expect(added[MON]).toEqual(['09:00-17:00', DEFAULT_TIMESPAN]);
    expect(removeSpan(added, MON, 1)[MON]).toEqual(['09:00-17:00']);
  });

  it('removing the last span closes the day', () => {
    expect(removeSpan(hours(), MON, 0)[MON]).toEqual([]);
  });

  it('removes only the span at that index of a split day', () => {
    expect(removeSpan(hours(), 'fri_open_close', 0)['fri_open_close']).toEqual([
      '13:00-17:00'
    ]);
  });

  it('updates one span of a split day', () => {
    const next = updateSpan(hours(), 'fri_open_close', 1, '14:00-18:00');
    expect(next['fri_open_close']).toEqual(['09:00-12:00', '14:00-18:00']);
  });
});

describe('splitting and joining a span', () => {
  it('round trips', () => {
    expect(splitSpan('09:00-17:00')).toEqual({open: '09:00', close: '17:00'});
    expect(joinSpan('09:00', '17:00')).toBe('09:00-17:00');
  });

  it('keeps sunrise and sunset, which are valid values', () => {
    expect(splitSpan('sunrise-sunset')).toEqual({
      open: 'sunrise',
      close: 'sunset'
    });
  });

  it('falls back to midnight for a malformed span', () => {
    expect(splitSpan('')).toEqual({open: '00:00', close: '00:00'});
    expect(splitSpan(undefined as any)).toEqual({
      open: '00:00',
      close: '00:00'
    });
  });
});

describe('day presets', () => {
  it('splits the week into weekdays and weekend', () => {
    expect(WEEKDAYS).toEqual([
      MON,
      'tue_open_close',
      'wed_open_close',
      'thu_open_close',
      'fri_open_close'
    ]);
    expect(WEEKEND).toEqual(['sat_open_close', SUN]);
  });
});

describe('setDays', () => {
  const WED_TO_FRI = ['wed_open_close', 'thu_open_close', 'fri_open_close'];

  it('sets only the given days', () => {
    const next = setDays(hours(), WED_TO_FRI, ['10:00-18:00']);
    WED_TO_FRI.forEach((key) => expect(next[key]).toEqual(['10:00-18:00']));
    expect(next[MON]).toEqual(['09:00-17:00']);
    expect(next[SUN]).toEqual([]);
  });

  it('replaces split hours with the new span', () => {
    const next = setDays(hours(), ['fri_open_close'], ['09:00-17:00']);
    expect(next.fri_open_close).toEqual(['09:00-17:00']);
  });

  it('sets days to 24 hours', () => {
    const next = setDays(hours(), WEEKEND, [OPEN_24_HRS]);
    WEEKEND.forEach((key) => expect(isOpen24(next, key)).toBe(true));
  });

  it('closes days with no spans', () => {
    const next = setDays(hours(), WEEKDAYS, []);
    expect(openDayCount(next)).toBe(0);
  });

  it('does not share the array between days', () => {
    const next = setDays(hours(), ALL_DAYS, ['09:00-17:00']);
    expect(next[MON]).not.toBe(next[SUN]);
    expect(next[SUN]).toEqual(['09:00-17:00']);
    expect(next[SUN]).toEqual(['09:00-17:00']);
  });
});

describe('hasAnyHours', () => {
  it('is false when nothing is known', () => {
    expect(hasAnyHours({})).toBe(false);
    expect(
      hasAnyHours({mon_open_close: [], sun_open_close: []})
    ).toBe(false);
  });

  it('is true as soon as one day is open', () => {
    expect(hasAnyHours(hours())).toBe(true);
  });
});

describe('formatSpan', () => {
  it('formats a clock span for reading', () => {
    expect(formatSpan('09:00-17:00')).toBe('9:00AM - 5:00PM');
  });

  it('handles midnight and noon', () => {
    expect(formatSpan('00:00-12:30')).toBe('12:00AM - 12:30PM');
  });

  it('capitalises sunrise and sunset rather than mangling them', () => {
    expect(formatSpan('sunrise-sunset')).toBe('Sunrise - Sunset');
  });

  it('names the all day case', () => {
    expect(formatSpan(OPEN_24_HRS)).toBe('Open 24 hours');
  });
});
